"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from "react";

import { cn } from "@/lib/utils";

const RadioGroup = forwardRef<
  ComponentRef<typeof RadioGroupPrimitive.Root>,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    className={cn("grid gap-2", className)}
    {...props}
    ref={ref}
  />
));
RadioGroup.displayName = "RadioGroup";

/* ── Size config (Figma spec) ──
 * md: 24px hitArea, 20px ring, 12px dot
 * sm: 20px hitArea, 16px ring, 10px dot
 */
const SIZE_CONFIG = {
  medium: { container: "w-6 h-6", ring: "w-5 h-5 border-2", dot: "w-3 h-3" },
  small: { container: "w-5 h-5", ring: "w-4 h-4 border", dot: "w-2 h-2" },
} as const;

type RadioSize = keyof typeof SIZE_CONFIG;

export interface RadioButtonProps
  extends ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  variant?: "primary";
  size?: RadioSize;
}

const RadioButton = forwardRef<
  ComponentRef<typeof RadioGroupPrimitive.Item>,
  RadioButtonProps
>(
  (
    { className, variant: _variant = "primary", size = "medium", children, ...props },
    ref
  ) => {
    const s = SIZE_CONFIG[size];

    return (
      <label
        className={cn(
          "inline-flex items-center gap-2 cursor-pointer",
          props.disabled && "cursor-default"
        )}
      >
        <RadioGroupPrimitive.Item
          ref={ref}
          className={cn(
            "group peer shrink-0 relative flex items-center justify-center",
            s.container,
            "disabled:opacity-[0.15]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700/50",
            className
          )}
          {...props}
        >
          {/* Visual ring */}
          <span
            className={cn(
              "flex items-center justify-center rounded-full",
              "border-gray-700 bg-white",
              "group-data-[state=checked]:border-primary-700",
              s.ring
            )}
            aria-hidden
          >
            {/* Inner dot — only visible when checked via Indicator */}
          </span>
          <RadioGroupPrimitive.Indicator
            className={cn("absolute flex items-center justify-center", s.container)}
          >
            <span className={cn("block rounded-full bg-primary-700", s.dot)} />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
        {children && (
          <span className="text-[15px] leading-[1.4] text-gray-900 select-none">
            {children}
          </span>
        )}
      </label>
    );
  }
);
RadioButton.displayName = "RadioButton";

export { RadioButton, RadioGroup };
